import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Modal, Spin } from "antd";
import { globalModel } from "../global/GlobalModel";
import { SpaceTabBar } from "./common/SpaceTabBar";
import { SpaceHomePage } from "./home/SpaceHomePage";
import { SpaceMinePage } from "./mine/SpaceMinePage";
import {
  findMembershipProduct,
  SpaceProductSection,
  tryOpenH5ProductService,
} from "./product/SpaceProductSection";
import { isValidSpaceRoute, SpaceModel } from "./SpaceModel";
import { useDocumentSpaceTheme } from "../utils/useDocumentSpaceTheme";
import { useSpaceTabBarLayoutVars } from "../utils/useSpaceTabBarLayoutVars";
import "./SpaceStyle.css";

const SpacePage = observer(() => {
  const { spaceId, tab } = useParams<{ spaceId: string; tab: string }>();
  const navigate = useNavigate();
  const model = useMemo(() => new SpaceModel(), []);
  const subscribeSectionHintRef = useRef<HTMLParagraphElement>(null);

  useDocumentSpaceTheme(true);
  useSpaceTabBarLayoutVars(true);

  useEffect(() => {
    if (!spaceId || !/^\d{8}$/.test(spaceId)) {
      return;
    }
    model.setSpaceId(spaceId);
    void model.loadConfig();
  }, [spaceId, model]);

  const isLoggedIn = globalModel.isLoggedIn;

  useEffect(() => {
    if (isLoggedIn) {
      void globalModel.loadFollowSpaces();
    }
  }, [spaceId, isLoggedIn]);

  useEffect(() => {
    if (!model.loaded || !model.modules.length) {
      return;
    }
    void model.refreshSubscriptionMerge();
  }, [spaceId, model, model.loaded, model.modules.length, isLoggedIn]);

  useEffect(() => {
    if (!globalModel.isLoggedIn) {
      model.resetFavorState();
      return;
    }
    void model.loadFavorState();
  }, [spaceId, globalModel.isLoggedIn, model]);

  const activeTab = tab ?? "home";

  useEffect(() => {
    if (!model.loaded || !spaceId) {
      return;
    }
    if (activeTab === "home") {
      return;
    }
    const current = model.tabs.find((t) => t.key === activeTab);
    const productModule = findMembershipProduct(model.membershipProducts, activeTab);
    const requiresLogin =
      current?.requiresLogin === true ||
      activeTab === "mine" ||
      productModule != null;
    if (requiresLogin && !isLoggedIn) {
      const redirect = encodeURIComponent(`/space/${spaceId}/${activeTab}`);
      navigate(`/login?redirect=${redirect}`, { replace: true });
    }
  }, [activeTab, isLoggedIn, model.loaded, model.tabs, model.membershipProducts, navigate, spaceId]);

  const onLoginRequest = useCallback(() => {
    if (!spaceId) {
      return;
    }
    const redirect = encodeURIComponent(`/space/${spaceId}/home`);
    navigate(`/login?redirect=${redirect}`);
  }, [navigate, spaceId]);

  const onSubscribeScroll = useCallback(() => {
    const el = subscribeSectionHintRef.current;
    if (!el) {
      return;
    }
    const main = document.querySelector<HTMLElement>(".lf-h5-space-main");
    if (!main) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    const top =
      el.getBoundingClientRect().top -
      main.getBoundingClientRect().top +
      main.scrollTop -
      12;
    main.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  }, []);

  const onFollowClick = useCallback(() => {
    void model.submitFavor(true);
  }, [model]);

  const onRequestUnfollow = useCallback(() => {
    Modal.confirm({
      title: "取消关注",
      content: "确定取消关注该空间？",
      okText: "确认",
      cancelText: "取消",
      centered: true,
      onOk: () => model.submitFavor(false),
    });
  }, [model]);

  const handleTabChange = useCallback(
    (key: string) => {
      if (!spaceId) {
        return;
      }
      const target = model.tabs.find((t) => t.key === key);
      if (!target) {
        return;
      }
      if (target.requiresLogin && !isLoggedIn) {
        const redirect = encodeURIComponent(`/space/${spaceId}/${key}`);
        navigate(`/login?redirect=${redirect}`);
        return;
      }
      if (key !== "home" && key !== "mine") {
        const product = findMembershipProduct(model.membershipProducts, key);
        if (!tryOpenH5ProductService(product)) {
          return;
        }
      }
      navigate(`/space/${spaceId}/${key}`);
    },
    [spaceId, model.tabs, model.membershipProducts, navigate, isLoggedIn]
  );

  if (!spaceId || !/^\d{8}$/.test(spaceId)) {
    return <Navigate to="/" replace />;
  }

  if (!model.loaded) {
    return (
      <div className="space-page-loading">
        <Spin />
      </div>
    );
  }

  if (model.loadError) {
    return <div className="space-page-error">{model.loadError}</div>;
  }

  if (!isValidSpaceRoute(activeTab, model.tabs, model.membershipProducts)) {
    return <Navigate to={`/space/${spaceId}/home`} replace />;
  }

  const currentTab = model.tabs.find((t) => t.key === activeTab);
  const productModule = findMembershipProduct(model.membershipProducts, activeTab);

  return (
    <div className="lf-h5-space-shell">
      <main className="lf-h5-space-main">
        {activeTab === "home" ? (
          <SpaceHomePage
            spaceId={spaceId}
            profile={model.profile}
            homeData={model.homeData}
            membershipProducts={model.membershipProducts}
            isLoggedIn={isLoggedIn}
            favorStatus={model.favorStatus}
            subscribeSectionHintRef={subscribeSectionHintRef}
            onLoginRequest={onLoginRequest}
            onFollow={onFollowClick}
            onRequestUnfollow={onRequestUnfollow}
            onSubscribeScroll={onSubscribeScroll}
          />
        ) : activeTab === "mine" ? (
          <SpaceMinePage spaceId={spaceId} />
        ) : productModule ? (
          <SpaceProductSection
            spaceId={spaceId}
            module={productModule}
            spaceProfile={model.profile}
            membershipProducts={model.membershipProducts}
            isLoggedIn={isLoggedIn}
            onLoginRequest={onLoginRequest}
            onSubscribeScroll={onSubscribeScroll}
          />
        ) : (
          <div className="space-product-page">
            <div className="space-product-placeholder">
              <p className="space-product-placeholder-title">{currentTab?.label ?? "产品"}</p>
              <p className="space-product-placeholder-desc">产品不存在或暂不可访问</p>
            </div>
          </div>
        )}
      </main>

      <SpaceTabBar
        tabs={model.tabs}
        activeKey={activeTab}
        onChange={handleTabChange}
      />
    </div>
  );
});

export default SpacePage;
